import { useEffect, useState } from "react";
import { Copy, ExternalLink, Send, Pencil, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { SimpleSelect } from "@/components/Filters";
import { StatusPill, LateCell, ActionTag, COURIERS } from "@/components/shipments/ShipCommon";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { dateLabel } from "@/lib/format";

const EDIT_FIELDS = ["order_id", "order_date", "ship_date", "customer_name", "phone", "product", "status", "expected_delivery",
  "courier", "tracking_id", "courier_remark", "remarks"];

const Info = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-sm font-medium break-words">{children || "—"}</div></div>
);

const when = (ts) => (ts ? (ts.length > 10 ? new Date(ts).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : dateLabel(ts)) : "");

const NoteItem = ({ f, idx, sid, canEdit, isAdmin, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(f.text);
  const save = async () => {
    try { onSaved((await api.patch(`/shipments/${sid}/followups/${idx}`, { text })).data); setEditing(false); toast.success("Note updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this follow-up note?")) return;
    try { onSaved((await api.delete(`/shipments/${sid}/followups/${idx}`)).data); toast.success("Note deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="group rounded-md border border-border bg-slate-50 px-3 py-2" data-testid={`followup-item-${idx}`}>
      {editing ? (
        <div className="flex gap-2">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid={`followup-edit-input-${idx}`} />
          <div className="flex flex-col gap-1">
            <Button size="icon" className="h-7 w-7" disabled={!text.trim()} onClick={save} data-testid={`followup-edit-save-${idx}`}><Check className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(false); setText(f.text); }}><X className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm">{f.text}</div>
          <div className="flex shrink-0 gap-0.5">
            {canEdit && <button className="p-1 text-muted-foreground hover:text-primary" title="Edit note" onClick={() => setEditing(true)} data-testid={`followup-edit-btn-${idx}`}><Pencil className="h-3.5 w-3.5" /></button>}
            {isAdmin && <button className="p-1 text-muted-foreground hover:text-red-600" title="Delete note" onClick={del} data-testid={`followup-delete-btn-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
      )}
      <div className="mt-0.5 text-[11px] text-muted-foreground">{f.user || f.label || "—"} · {when(f.timestamp)}{f.edited_by ? ` · edited by ${f.edited_by}` : ""}</div>
    </div>
  );
};

const Notes = ({ s, canEdit, isAdmin, onSaved }) => {
  const [text, setText] = useState("");
  const add = async () => {
    try { onSaved((await api.post(`/shipments/${s.id}/followups`, { text })).data); setText(""); toast.success("Follow-up note added"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      <Label>Follow-up history</Label>
      {canEdit && (
        <div className="flex gap-2">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add follow-up note…" data-testid="followup-input" />
          <Button size="icon" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white" disabled={!text.trim()} onClick={add} data-testid="followup-add-btn"><Send className="h-4 w-4" /></Button>
        </div>
      )}
      <div className="space-y-2" data-testid="followup-list">
        {s.followups.map((f, i) => ({ f, i })).reverse().map(({ f, i }) => (
          <NoteItem key={`${i}-${f.timestamp}`} f={f} idx={i} sid={s.id} canEdit={canEdit} isAdmin={isAdmin} onSaved={onSaved} />
        ))}
        {!s.followups.length && <div className="text-xs text-muted-foreground">No notes yet.</div>}
      </div>
    </div>
  );
};

const Fld = ({ label, className = "", children }) => <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>;

const EditForm = ({ s, statuses, canEdit, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => setF(Object.fromEntries(EDIT_FIELDS.map((k) => [k, s[k] ?? ""]))), [s]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const save = async (extra = {}) => {
    const body = { ...Object.fromEntries(EDIT_FIELDS.filter((k) => (f[k] ?? "") !== (s[k] ?? "")).map((k) => [k, f[k]])), ...extra };
    if (!Object.keys(body).length) return toast.info("Nothing changed");
    try { onSaved((await api.patch(`/shipments/${s.id}`, { ...body, expected_updated_at: s.updated_at })).data); toast.success("Shipment saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = (k, props = {}) => <Input disabled={!canEdit} value={f[k] || ""} onChange={set(k)} data-testid={`drawer-${k.replace(/_/g, "-")}-input`} {...props} />;
  return (
    <div className="grid grid-cols-2 gap-3">
      <Fld label="Order ID">{inp("order_id", { className: "font-mono" })}</Fld>
      <Fld label="Status"><SimpleSelect value={f.status || s.status} onChange={set("status")} testid="drawer-status-select" className="h-9 w-full" options={statuses.map((x) => [x, x])} /></Fld>
      <Fld label="Order date">{inp("order_date", { type: "date" })}</Fld>
      <Fld label="Ship date">{inp("ship_date", { type: "date" })}</Fld>
      <Fld label="Customer">{inp("customer_name")}</Fld>
      <Fld label="Phone">{inp("phone", { inputMode: "tel" })}</Fld>
      <Fld label="Product" className="col-span-2">{inp("product")}</Fld>
      <Fld label="Courier">{inp("courier", { list: "courier-list" })}<datalist id="courier-list">{COURIERS.map((c) => <option key={c} value={c} />)}</datalist></Fld>
      <Fld label="AWB / Tracking ID">{inp("tracking_id", { className: "font-mono" })}</Fld>
      <Fld label="Expected delivery">{inp("expected_delivery", { type: "date" })}</Fld>
      <Fld label="Courier remark">{inp("courier_remark")}</Fld>
      <Fld label="Internal remarks" className="col-span-2"><Textarea rows={2} disabled={!canEdit} value={f.remarks || ""} onChange={set("remarks")} data-testid="drawer-remarks-input" /></Fld>
      {s.status === "RTO" && canEdit && (
        <label className="col-span-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium" data-testid="drawer-rto-received">
          <Checkbox checked={s.rto_received} onCheckedChange={(v) => v && save({ rto_received: true })} /> RTO received back in warehouse
        </label>
      )}
      {canEdit && <Button onClick={() => save()} className="col-span-2 bg-[#071434] hover:bg-[#0d1f4d]" data-testid="drawer-save-btn">Save changes</Button>}
    </div>
  );
};

export const ShipmentDrawer = ({ id, statuses, onClose, onChanged }) => {
  const { can, isAdmin } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { setS(null); if (id) api.get(`/shipments/${id}`).then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  const saved = (d) => { setS(d); onChanged(); };
  const copy = () => navigator.clipboard.writeText(s.tracking_id).then(() => toast.success("AWB copied"));
  const del = async () => {
    if (!window.confirm(`Delete shipment ${s.order_id}${s.tracking_id ? ` (AWB ${s.tracking_id})` : ""}? This cannot be undone.`)) return;
    try { await api.delete(`/shipments/${s.id}`); toast.success("Shipment deleted"); onChanged(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto app-light" data-testid="shipment-drawer">
        {!s ? <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div> : (
          <div className="space-y-5">
            <SheetHeader className="text-left">
              <SheetTitle className="font-mono text-base" data-testid="drawer-order-id">{s.order_id}</SheetTitle>
              <div className="flex flex-wrap items-center gap-2"><StatusPill status={s.status} testid="drawer-status" /><ActionTag action={s.action} />
                <span className="text-xs text-muted-foreground">Days late: <LateCell days={s.days_late} /></span></div>
            </SheetHeader>
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3">
              <Info label="Channel">{s.channel}</Info><Info label="Expected (live)">{dateLabel(s.expected)}</Info>
              <Info label="Courier">{s.courier}</Info>
              <Info label="AWB / Tracking ID"><span className="font-mono" data-testid="drawer-awb">{s.tracking_id || "No AWB"}</span></Info>
              <div className="col-span-2 flex flex-wrap items-center gap-2">
                {s.phone && <a href={`tel:${s.phone}`} className="text-xs font-semibold text-blue-700 underline">Call {s.phone}</a>}
                {s.tracking_id && <Button size="sm" variant="outline" onClick={copy} data-testid="drawer-copy-awb"><Copy className="h-3.5 w-3.5" /> Copy AWB</Button>}
                {s.track_url && <a href={s.track_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline" data-testid="drawer-track-link">Track on courier site <ExternalLink className="h-3 w-3" /></a>}
              </div>
            </div>
            <EditForm s={s} statuses={statuses} canEdit={can("shipments", "edit")} onSaved={saved} />
            <Notes s={s} canEdit={can("shipments", "edit")} isAdmin={isAdmin} onSaved={saved} />
            {isAdmin && (
              <Button variant="outline" onClick={del} className="w-full border-red-300 text-red-700 hover:bg-red-50" data-testid="drawer-delete-btn">
                <Trash2 className="h-4 w-4" /> Delete this shipment</Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
